import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk/';
import { NavigationService } from '@frk/eds-components';
import {
  AppStateService,
  ProfileService,
  StockDataDTO,
  StockQuoteListResultDTO,
  StockTickerServiceService,
  WidenService,
} from '@services';
import { LinkService } from '@services/link.service';
import { CardContent, Link, LinkCollection } from '@types';
import { Logger } from '@utils/logger';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { symbols } from '@utils/il8n/currency';

import { TranslateService } from '@shared/translate/translate.service';
import { PreTitleWithTitleAndSummary } from '@marketing/side-bar/side-bar.component';
import { processLinkCollection } from '@utils/link-utils';

/**
 * Component Logger
 */
const logger = Logger.getLogger('BannerMultiPromoComponent');

/**
 * Promo Card Interface
 */
interface PromoCardDetails {
  title?: string;
  subTitle?: string;
  imageSrc?: string;
  linkBtnLabel?: string;
  linkBtnHref?: string;
  isNewWindow?: boolean;
  externalLink?: boolean;
  signInRequired?: boolean;
  stockPrice?: string;
  currency?: string;
  stockAdditionalContent?: string;
  hasThumbnail?: boolean;
  hasStockPrice?: boolean;
  theme?: string;
  quickLinks?: NavLink[];
  hasPromoQuickLinks?: boolean;
}

export interface MultiPromoLinkListItem {
  linkListItems?: LinkListItems[];
  linkListTitle?: string;
  linkListDescription?: string;
}

export interface SidebarLinkListContet {
  preTitleWithTitleAndSummary: PreTitleWithTitleAndSummary;
  linkCompound: Link[];
}

export interface LinkListItems {
  linkTitle?: string;
  href?: string;
  isExternal?: boolean;
  signInRequired?: boolean;
  isNewWindow?: boolean;
}

interface LinkListItem {
  description: string;
  link: {
    linkCollection: LinkCollection[];
  };
  image: {
    widenDocument: string;
  };
}
interface LinkListContent {
  title: string;
  summary: {
    content: string;
  };
  linkListItem: LinkListItem[];
}

interface NavLink {
  text: string;
  href: string;
  isNewWindow?: boolean;
  externalLink?: string;
  description?: string;
  signInRequired?: boolean;
}

@Component({
  selector: 'ft-banner-multi-promo',
  templateUrl: './banner-multi-promo.component.html',
})
export class BannerMultiPromoComponent implements OnInit, OnDestroy {
  /**
   * The Bloomreach component
   */
  @Input() component!: BrComponent;

  /**
   * The Bloomreach page
   */
  @Input() page!: Page;

  /**
   * Object to be passed to EDS component
   */
  promoCards: PromoCardDetails[] = [];

  marginBottom: string;

  stockTicker: boolean;

  stockFeed: StockQuoteListResultDTO;

  isLoggedIn: boolean;

  private unsubscribe$: Subject<void> = new Subject<void>();

  public displaySidebar: boolean;
  public sidebarContent: MultiPromoLinkListItem = {
    linkListItems: [],
  };
  public links: NavLink[];

  public displayQuickLinksAsCard: boolean;
  public quickLinksTitle: string;

  /**
   * Constructor
   * @param widenService injected widen service
   */
  constructor(
    private appStateService: AppStateService,
    private linkService: LinkService,
    private widenService: WidenService,
    private navigationService: NavigationService,
    private profileService: ProfileService,
    private stockTickerServiceService: StockTickerServiceService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    this.profileService
      .isLoggedIn$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
      });
  }

  /**
   * On Init
   */
  ngOnInit() {
    this.marginBottom = this.component.getParameters()?.marginBottom;
    this.stockTicker = this.component.getParameters()?.stockTicker;
    this.displaySidebar = this.component.getParameters()?.displaySidebar;
    this.displayQuickLinksAsCard = this.component.getParameters()?.displayQuickLinksAsCard;

    this.buildQuickLink('quickLinks');

    if (this.stockTicker) {
      this.stockTickerServiceService
        ?.getStockData$()
        ?.pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (data: StockDataDTO) => {
            if (data) {
              this.stockFeed = data?.GetStockQuoteListResult[0];
            }
            this.initPromoCards();
          },
          (error) => {
            logger.warn('error', error);
            this.initPromoCards();
          }
        );
    } else {
      this.initPromoCards();
    }
    this.cdr.detectChanges();
    logger.debug('ngOnInit complete');
  }

  /**
   * Init promo cards from CardContent documents
   */
  initPromoCards() {
    // This component has 2 document parameters
    if (this.displaySidebar) {
      this.initLinkListItem('documentSidebar');
      this.initPromoCard('documentOne');
    } else {
      // This component has 3 document parameters
      this.initPromoCard('documentOne');
      if (this.displayQuickLinksAsCard) {
        this.promoCards.push({
          title: this.quickLinksTitle,
          hasPromoQuickLinks: this.displayQuickLinksAsCard,
          quickLinks: this.links,
        });
      }
      if (!this.stockTicker) {
        this.initPromoCard('documentTwo');
      } else {
        if (this.stockFeed) {
          this.promoCards.push({
            title: this.translateService.instant('products.ticker-nyse'),
            hasThumbnail: false,
            hasStockPrice: true,
            stockPrice: `${symbols.get('USD')}${this.stockFeed?.TradePrice}`,
            currency: this.translateService.instant('products.ticker-usd'),
            stockAdditionalContent: `<div class="stock-value-change">${
              this.stockFeed?.Change
            } (${
              this.stockFeed?.PercChange
            }%)</div> <small>${this.translateService.instant(
              'products.ticker-msg'
            )}</small>`,
            linkBtnHref: this.translateService.instant('products.ticker-url'),
          });
        }
      }
      if (!this.displayQuickLinksAsCard) {
        this.initPromoCard('documentThree');
      }
    }
  }

  /**
   * Create `PromoCardDetails` from cardContent document
   */
  initPromoCard(documentName: string): void {
    const cardContent = this.getCardContent(documentName);

    if (cardContent) {
      logger.debug(cardContent);
      this.promoCards.push({
        title: cardContent.title,
        subTitle: cardContent.content,
        imageSrc: this.widenService.getWidenAssetUrl(
          cardContent.image?.widenAsset
        ),
        linkBtnHref: this.processLink(cardContent),
        isNewWindow:
          cardContent.linkCompound?.linkCollection[0]?.target === '_blank',
        externalLink: cardContent.linkCompound?.linkCollection[0]?.external,
        linkBtnLabel: cardContent.linkCompound?.linkCollection[0]?.displayText,
        signInRequired:
          cardContent.linkCompound?.linkCollection[0]?.signInLink &&
          !this.isLoggedIn,
      });
    }
  }

  private initLinkListItem(documentName: string): void {
    const content: SidebarLinkListContet = this.getSidebarContent(documentName);
    if (content) {
      this.sidebarContent.linkListTitle =
        content.preTitleWithTitleAndSummary?.string1;
      this.sidebarContent.linkListDescription =
        content.preTitleWithTitleAndSummary?.html?.value;
      content.linkCompound.forEach((linkdata) => {
        if (linkdata.linkCollection.length) {
          const linkDetails = processLinkCollection(
            linkdata?.linkCollection[0],
            this.page
          );
          this.sidebarContent.linkListItems.push({
            href: linkDetails?.url,
            linkTitle: linkDetails?.displayText,
            isExternal: linkDetails?.external,
            signInRequired: linkDetails?.signInLink,
            isNewWindow: linkDetails?.target === '_blank',
          });
        }
      });
    }
  }

  /**
   * Process the different link types
   * @param cardContent card content
   */
  processLink(cardContent: CardContent): string {
    // Process link
    let link;
    if (cardContent.linkCompound?.linkCollection?.length > 0) {
      link = this.linkService.getCTALink(
        this.page,
        cardContent.linkCompound?.linkCollection[0],
        this.appStateService?.getHomePageUrl()
      );
    }
    logger.debug('link: ' + link);
    return link;
  }

  /**
   * Get card document from page model
   */
  private getCardContent(documentName: string): CardContent {
    let doc = this.component.getModels<DocumentModels>()[documentName];
    doc = doc && this.page.getContent(doc);
    return doc?.getData<CardContent>();
  }

  private getSidebarContent(documentName: string): SidebarLinkListContet {
    let doc = this.component.getModels<DocumentModels>()[documentName];
    doc = doc && this.page.getContent(doc);
    return doc?.getData<SidebarLinkListContet>();
  }

  public buildQuickLink(documentName: string): void {
    const document = this.component.getModels<DocumentModels>()[documentName];
    const content = this.page.getContent(document)?.getData<LinkListContent>();
    if (content) {
      this.quickLinksTitle = content.title;
      this.links = this.getLinkItems(content);
    }
  }

  /**
   * Format Quick Links
   */
  public getLinkItems(content: LinkListContent): NavLink[] {
    const links = content?.linkListItem?.map((item: LinkListItem) => {
      const link: LinkCollection = item?.link?.linkCollection[0];
      const linkItem = {
        text: link.displayText,
        href: this.linkService.getCTALink(
          this.page,
          link,
          this.appStateService?.getHomePageUrl()
        ),
        isNewWindow: link?.target === '_blank' ? true : false,
        externalLink: this.getExternalLink(
          link?.external,
          link?.externalSuffix
        ),
        description: item.description,
        signInRequired: link.signInLink,
      };
      return linkItem;
    });
    return links;
  }

  /**
   * Set external link class
   * @param external - is link external true|false
   * @param externalSuffix - suffix to add to external link
   */
  private getExternalLink(external: boolean, externalSuffix: string): string {
    if (external) {
      const externalLink = 'external-link';
      if (externalSuffix) {
        // Adding external link suffix
        return externalLink + '--' + externalSuffix;
      }
      return externalLink;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
